import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Search, Download, Sparkles, Trash2, Heart, BookOpen, X } from "lucide-react";
import axios from "axios";
import "../../../assets/css/gcstudent/savedPdfs/SavedPDFS.css";
import AppContext from "../../../context/AppContext";
import Swal from "sweetalert2";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ── PDF Reader Modal ── */
const PDFReaderModal = ({ pdf, show, onClose }) => {
  if (!show || !pdf) return null;
  return (
    <div
      className="gcStudent-pdf-reader-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="gcStudent-pdf-reader-modal">
        {/* Header */}
        <div className="gcStudent-pdf-reader-modal__header">
          <div className="gcStudent-pdf-reader-modal__header-info">
            <BookOpen size={18} strokeWidth={1.8} />
            <div>
              <h6 className="gcStudent-pdf-reader-modal__title">{pdf.title}</h6>
              <p className="gcStudent-pdf-reader-modal__author">{pdf.author}</p>
            </div>
          </div>
          <button className="gcStudent-pdf-reader-modal__close" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* PDF Viewer Area */}
        <div className="gcStudent-pdf-reader-modal__body">
          {pdf.pdfUrl ? (
            <iframe
              src={pdf.pdfUrl}
              title={pdf.title}
              className="gcStudent-pdf-reader-modal__iframe"
            />
          ) : (
            <div className="gcStudent-pdf-reader-modal__placeholder">
              <BookOpen size={52} strokeWidth={1.2} />
              <p>PDF preview not available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   Saved PDFs Page
══════════════════════════════════════ */

const ITEMS_PER_PAGE = 8; // yahan se page size control hoti hai (grid layout, isliye 8)

const SavedPDFSPAGE = () => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);        // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false); // page switch / search loading (sirf visible cards k liye)
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [showReader, setShowReader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  const { baseUrl, baseUrlForFiles, authToken, loggedInUser } = useContext(AppContext);



  /*
    Raw shape coming from GET /student/notes/getSavedEbooks/{student_id}:
    {
      id: <favourite record id>,   ← used for delete (removeSavedEbook/{id})
      lbstudent_id, lbebook_id, ...
      ebook: { id, title, author, dept, pdf_file, img }   ← eager-loaded relation
    }
  */
  const mapSavedItem = (fav) => {
    const book = fav.ebook || {};
    return {
      id: fav.id, // favourite record id — NOT the ebook id
      // baseUrlForFiles already ends with /storage, so don't prepend it again here
      image: book.img ? `${baseUrlForFiles}/${book.img}` : null,
      title: book.title,
      author: book.author,
      category: book.dept || "General",
      savedDate: fav.created_at
        ? new Date(fav.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
      pdfUrl: book.pdf_file ? `${baseUrlForFiles}/ebooks/${book.pdf_file}` : null,
    };
  };

  /* ── Fetch saved PDFs ──
     NOTE: getSavedEbooks route still lives under /student/notes/ in api.php
     (only saveEbook/removeSavedEbook were moved to /student/ebooks/) */
  useEffect(() => {
    if (!loggedInUser?.id) return;

    setLoading(true);
   axios.get(`${baseUrl}/student/ebooks/getSavedEbooks/${loggedInUser.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setPdfs((res.data.ebooks || []).map(mapSavedItem));
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Filtered list ── */
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return pdfs;
    return pdfs.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.author?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
    );
  }, [pdfs, searchQuery]);

  /* ── Pagination slice (sirf current page ka data grid mein jayega) ── */
  const paginatedPdfs = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /* ── Sirf visible cards ki loading dikhayein:
     - Jab search se list filter ho
     - Jab pagination se dusre page par jayen
     Poora grid ek sath "pop" nahi hoga, sirf jitne cards is waqt
     screen par show hone hain unki hi loading chalegi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  const handleRead = (pdf) => {
    setSelectedPDF(pdf);
    setShowReader(true);
  };

  const handleCloseReader = () => {
    setShowReader(false);
    setTimeout(() => setSelectedPDF(null), 300);
  };

  const handleDownload = (pdf) => {
    if (!pdf.pdfUrl) {

  Swal.fire({
    icon: "warning",
    title: "PDF Not Available",
    text: "This PDF is currently unavailable.",
    confirmButtonColor: "var(--primary-color)",
  });

  return;
}
    window.open(pdf.pdfUrl, "_blank");
  };

  const handleFlash = () => {

  Swal.fire({
    icon: "info",
    title: "Coming Soon",
    text: "AI Summary feature will be available soon.",
    confirmButtonColor: "var(--primary-color)",
  });

};

  /* ── Unsave / Remove ── */
  const handleUnsave = (id, title) => {

  axios
    .delete(`${baseUrl}/student/ebooks/removeSavedEbook/${id}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })

    .then((res) => {

      if (res.data.success === true) {

        setPdfs((prev) => prev.filter((p) => p.id !== id));

        Swal.fire({
          icon: "success",
          title: "Removed Successfully!",
          text: `"${title}" has been removed from Saved PDFs.`,
          confirmButtonColor: "var(--primary-color)",
          timer: 1800,
          showConfirmButton: false,
        });

      }

    })

    .catch((err) => {

      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Remove Failed",
        text: "Could not remove this ebook. Please try again.",
        confirmButtonColor: "var(--primary-color)",
      });

    });

};

  return (
    <div className="gcStudent-pdfs-page">
      {/* Page heading */}
      <h2 className="gcStudent-pdf-heading">My Saved PDFs</h2>
      <p className="gcStudent-pdf-subtitle">
        Your wishlist of saved e-books from the digital library.
      </p>

      {/* Search bar */}
      <div className="gcStudent-pdf-search-wrap">
        <Search size={17} className="gcStudent-pdf-search-icon" />
        <input
          className="gcStudent-pdf-search-input"
          placeholder="Search saved PDFs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Saved PDFs loading, Please wait..." />}

      {/* Cards grid */}
      {!loading && (
        <>
          <div className="gcStudent-pdf-grid">
            {viewLoading ? (
              /* ── Page switch / search loading — sirf is section ki, baaki page affect nahi hota ── */
              <Loader message="Loading saved PDFs..." />
            ) : filtered.length === 0 ? (
              <div className="gcStudent-pdf-empty">
                <div className="gcStudent-pdf-empty-icon">📄</div>
                <p>No saved PDFs found.</p>
              </div>
            ) : (
              paginatedPdfs.map((pdf) => (
                <div className="gcStudent-pdf-card" key={pdf.id}>
                  {/* Cover image */}
                  <div className="gcStudent-pdf-img-wrap">
                    {pdf.image ? (
                      <img
                        className="gcStudent-pdf-img"
                        src={pdf.image}
                        alt={pdf.title}
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className="gcStudent-pdf-img"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(64,148,174,0.08)",
                        }}
                      >
                        <BookOpen size={42} strokeWidth={1.2} style={{ opacity: 0.35 }} />
                      </div>
                    )}
                    {/* Favourite heart */}
                    <button
                      className="gcStudent-pdf-fav-btn"
                      aria-label="Remove from favourites"
                      onClick={() => handleUnsave(pdf.id, pdf.title)}
                    >
                      <Heart size={15} fill="#fff" color="#fff" />
                    </button>
                  </div>

                  {/* Card body */}
                  <div className="gcStudent-pdf-card-body">
                    <span className="gcStudent-pdf-category">{pdf.category}</span>
                    <p className="gcStudent-pdf-book-title m-0">{pdf.title}</p>
                    <p className="gcStudent-pdf-book-author m-0">{pdf.author}</p>
                    <p className="gcStudent-pdf-saved-date">Saved: {pdf.savedDate}</p>

                    {/* Action buttons */}
                    <div className="gcStudent-pdf-actions">
                      {/* Read */}
                      <button className="gcStudent-pdf-btn-read" onClick={() => handleRead(pdf)}>
                        <BookOpen size={15} strokeWidth={2} />
                        Read
                      </button>

                      {/* Download */}
                      <button
                        className="gcStudent-pdf-icon-btn"
                        title="Download"
                        onClick={() => handleDownload(pdf)}
                      >
                        <Download size={15} strokeWidth={2} />
                      </button>

                      {/* AI Summary */}
                      <button
                        className="gcStudent-pdf-icon-btn gcStudent-pdf-flash"
                        title="AI Summary"
                        onClick={handleFlash}
                      >
                        <Sparkles size={15} strokeWidth={2} />
                      </button>

                      {/* Delete */}
                      <button
                        className="gcStudent-pdf-icon-btn gcStudent-pdf-delete"
                        title="Delete"
                        onClick={() => handleUnsave(pdf.id, pdf.title)}
                      >
                        <Trash2 size={15} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}


      {/* PDF Reader Modal */}
      <PDFReaderModal pdf={selectedPDF} show={showReader} onClose={handleCloseReader} />
    </div>
  );
};

export default SavedPDFSPAGE;